// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dropdown button component for asset-specific actions.
 * This module provides a menu of contextual actions that can be
 * performed on an asset, such as scrapping or restoring from scrap.
 * The component adapts its available actions based on the asset's
 * current state and relationships.
 *
 * Key features:
 * - Context-aware action menu
 * - Confirmation dialogs
 * - Tooltip guidance
 * - State-based toggling
 * - Project-aware disabling
 * - Responsive positioning
 * - Accessibility support
 */
import { PartialAsset } from '@/asset/api/types';
import { AssetScrapDialog } from '@/asset/components/asset-scrap-dialog';
import { AssetUnscrapDialog } from '@/asset/components/asset-unscrap-dialog';
import { ActionMenuItem } from '@/components/action-menu-item';
import {
  ArrowDropDownRounded,
  DeleteRounded,
  DownloadRounded,
  RestoreFromTrashRounded,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Button, Menu, Tooltip } from '@mui/material';
import { useRef } from 'react';

/**
 * Represents the properties for a specific component or entity.
 *
 * @interface Props
 * @property {PartialAsset} asset - The asset associated with the component or entity.
 */
interface Props {
  asset: PartialAsset;
}

/**
 * A dropdown button component that provides contextual asset actions.
 * Displays a menu of available actions based on the asset's current state,
 * with appropriate dialogs for confirming destructive actions. The component
 * automatically disables certain actions based on the asset's relationships
 * and provides helpful tooltips explaining why actions are unavailable.
 *
 * Features:
 * - Context-aware action availability
 * - Confirmation dialogs for destructive actions
 * - Tooltip explanations for disabled actions
 * - Responsive menu positioning
 * - State-based action toggling (scrap/unscrap)
 *
 * Available Actions:
 * - Scrap Asset: Mark as end-of-life (disabled if in project)
 * - Cancel Scrap: Return scrapped asset to service
 *
 * States:
 * - Normal: Shows scrap action if not in project
 * - Scrapped: Shows unscrap action
 * - Project-assigned: Disables scrapping with tooltip
 *
 * Usage example:
 * ```tsx
 * const asset = {
 *   key: 'asset-123',
 *   serial: 'SN123456',
 *   functionalStatus: 'FUNCTIONAL',
 *   inventoryDate: DateTime.now(),
 *   project: null,
 *   assignment: null
 * };
 *
 * return <AssetActionButton asset={asset} />;
 * ```
 *
 * @param props - Component configuration options
 */
export function AssetActionButton(props: Props) {
  const menu = useDisclosure();
  const scrapDialog = useDisclosure();
  const cancelDialog = useDisclosure();
  const ref = useRef<HTMLButtonElement>(null);

  // Disable scrapping for assets assigned to projects
  const isDisabled = props.asset.project !== null;
  const tooltipTitle = isDisabled
    ? 'This asset is assigned to a project so cannot be scrapped'
    : '';

  return (
    <>
      <AssetScrapDialog
        asset={props.asset}
        isOpen={scrapDialog.isOpen}
        onClose={scrapDialog.close}
      />
      <AssetUnscrapDialog
        asset={props.asset}
        isOpen={cancelDialog.isOpen}
        onClose={cancelDialog.close}
      />
      <Button
        ref={ref}
        variant="contained"
        color="secondary"
        onClick={menu.open}
        endIcon={<ArrowDropDownRounded fontSize="small" />}
      >
        More actions
      </Button>
      <Menu
        open={menu.isOpen}
        onClose={menu.close}
        onClick={menu.close}
        anchorEl={ref.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ disablePadding: true }}
        slotProps={{
          paper: { sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 } },
        }}
      >
        <ActionMenuItem
          icon={<DownloadRounded color="action" fontSize="small" />}
          label="Export asset"
          description="Export the details of this specific asset to an Excel (.xlsx) file"
          onClick={() => {
            const url = new URL(
              `/v1/export/assets/${props.asset.key}`,
              import.meta.env.VITE_API_URL
            );
            window.open(url, '_blank');
          }}
        />
        {props.asset.functionalStatus === 'SCRAPPED' ? (
          <ActionMenuItem
            onClick={cancelDialog.open}
            icon={<RestoreFromTrashRounded color="action" fontSize="small" />}
            label="Cancel asset scrap"
            description="Mark this asset as back in service and return to normal use (the asset was scrapped accidentally)"
          />
        ) : (
          <Tooltip arrow title={tooltipTitle}>
            <span>
              <ActionMenuItem
                onClick={scrapDialog.open}
                icon={<DeleteRounded color="action" fontSize="small" />}
                label="Scrap asset"
                description="Mark this asset as end-of-life and remove it completely (historical reporting will be preserved)"
                isDisabled={isDisabled}
              />
            </span>
          </Tooltip>
        )}
      </Menu>
    </>
  );
}
